import { useState, useEffect } from "react";
import ScanScreen from "./ScanScreen";
import InventoryScreen from "./InventoryScreen";
import ProgressScreen, { ProfileModal } from "./ProgressScreen";
import {
  auth, db, googleProvider,
  requestNotificationPermission, onForegroundMessage,
} from "./firebase";
import {
  signInWithPopup, signInWithEmailAndPassword,
  createUserWithEmailAndPassword, signOut, onAuthStateChanged,
} from "firebase/auth";
import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  onSnapshot, serverTimestamp, query, orderBy,
} from "firebase/firestore";

// ─── THEMES ──────────────────────────────────────────────────────────────────

const THEMES = {
  lavender: { name:"Lavender", primary:"#9B8EC4", secondary:"#C4B7E8", accent:"#7B6BB0", bg:"#F5F2FC", card:"#FFFFFF", soft:"#EDE8F9", text:"#2D1F5E", muted:"#8A7AB0", grad:"linear-gradient(135deg,#C4B7E8,#9B8EC4)" },
  rose:     { name:"Rose",     primary:"#E8829A", secondary:"#F4B8C6", accent:"#D05A75", bg:"#FEF2F5", card:"#FFFFFF", soft:"#FCE8ED", text:"#5C1A2A", muted:"#B07080", grad:"linear-gradient(135deg,#F4B8C6,#E8829A)" },
  sky:      { name:"Sky",      primary:"#5BA4CF", secondary:"#A8D4EF", accent:"#3A7DB5", bg:"#F0F7FD", card:"#FFFFFF", soft:"#E1F0FA", text:"#12334F", muted:"#5A8CAA", grad:"linear-gradient(135deg,#A8D4EF,#5BA4CF)" },
  sage:     { name:"Sage",     primary:"#7BAE7F", secondary:"#B5D4B7", accent:"#5A9060", bg:"#F2F8F2", card:"#FFFFFF", soft:"#E4F2E5", text:"#1A3D1E", muted:"#6A9A6E", grad:"linear-gradient(135deg,#B5D4B7,#7BAE7F)" },
  sunset:   { name:"Sunset",   primary:"#F0956A", secondary:"#F7C4A8", accent:"#D46B3A", bg:"#FDF6F2", card:"#FFFFFF", soft:"#FDEEE7", text:"#5C2410", muted:"#B07555", grad:"linear-gradient(135deg,#F7C4A8,#F0956A)" },
  midnight: { name:"Midnight", primary:"#7C6FE8", secondary:"#5A4FCF", accent:"#A090FF", bg:"#12111F", card:"#1E1C30", soft:"#2A2740", text:"#E8E5FF", muted:"#8A85C0", grad:"linear-gradient(135deg,#5A4FCF,#7C6FE8)" },
};

const LENS_TYPES = ["Soft Daily","Soft Bi-Weekly","Soft Monthly","Ortho-K","RGP Hard","Scleral","Toric"];

// ─── Icon system ──────────────────────────────────────────────────────────────
const ITEM_ICONS_DEFAULT = {
  "Solution":"🧴","Lens case":"🥽","Lenses":"👓",
  "Enzyme cleaner":"🧪","Rewetting drops":"💧","Other":"📦",
};
const REM_ICONS_DEFAULT = {
  "Lens replacement":"👓","Solution restock":"🧴","Case replacement":"🥽",
  "Appointment":"📅","Inspection":"🔬","Custom":"🔔",
};
const ICON_CATS = {
  "Eye care":["👁️","🥽","👓","🔬","💊","🩺","🏥","🧬","⚗️","🫧"],
  "Bottles":["🧴","💊","🫙","🧪","🍶","🫗","🧂","🫧","🪣","🏺"],
  "Cases":["🥽","📦","🗃️","🎁","👜","🧰","🪆","🗄️","📫","🪤"],
  "Drops":["💧","🌊","🫗","🪣","🚿","🫧","💦","🧊","🫙","🌧️"],
  "Cleaning":["🧹","🧼","✨","🪥","🧽","🪣","🫧","🧺","🧻","🪒"],
  "Calendar":["📅","⏰","🗓️","⌛","🔔","🔕","⏱️","🕐","📆","🗒️"],
  "Favourites":["⭐","❤️","🔥","✅","💯","🌟","💎","🏆","👑","🎯"],
};
function getItemIcon(key, customIcons) {
  return (customIcons && customIcons[key]) || ITEM_ICONS_DEFAULT[key] || REM_ICONS_DEFAULT[key] || "📦";
}
const SCHEDULES = {
  "Soft Daily":     { lens:1,   solution:7,  lensCase:30 },
  "Soft Bi-Weekly": { lens:14,  solution:14, lensCase:30 },
  "Soft Monthly":   { lens:30,  solution:14, lensCase:30 },
  "Ortho-K":        { lens:365, solution:30, lensCase:30, inspection:180 },
  "RGP Hard":       { lens:365, solution:30, lensCase:90, inspection:180 },
  "Scleral":        { lens:365, solution:30, lensCase:90, inspection:365 },
  "Toric":          { lens:30,  solution:14, lensCase:30 },
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────

const fmt      = (v) => (!v && v !== 0) ? "—" : v > 0 ? `+${v}` : String(v);
const daysAgo  = (d) => Math.floor((Date.now() - new Date(d)) / 86400000);
const daysTo   = (d) => Math.max(0, Math.floor((new Date(d) - Date.now()) / 86400000));
const todayISO = () => new Date().toISOString().split("T")[0];
const addDays  = (dateStr, n) => { const d = typeof dateStr === 'number' ? (n=dateStr, new Date()) : new Date(dateStr); d.setDate(d.getDate() + (n||0)); return d.toISOString().split('T')[0]; };

const userCol = (uid, col) => collection(db, "users", uid, col);
const userDoc = (uid, col, id) => doc(db, "users", uid, col, id);

function toast(msg, type = "info") {
  const el = document.createElement("div");
  const bg = type === "success" ? "#7BAE7F" : type === "error" ? "#E8829A" : "#9B8EC4";
  Object.assign(el.style, { position:"fixed", bottom:"96px", left:"50%", transform:"translateX(-50%)", background:bg, color:"#fff", padding:"10px 22px", borderRadius:"20px", fontSize:"13px", fontWeight:"600", zIndex:"9999", boxShadow:"0 4px 16px rgba(0,0,0,0.18)", transition:"opacity 0.3s", whiteSpace:"nowrap", maxWidth:"90vw", textAlign:"center" });
  el.textContent = msg;
  document.body.appendChild(el);
  setTimeout(() => { el.style.opacity = "0"; setTimeout(() => el.remove(), 300); }, 2800);
}

// Convert image blob to compressed base64 and store directly in Firestore.
// No Firebase Storage or third-party service needed — stays within free tier.
// Compresses to max 1200px JPEG at 82% quality (~100-200KB), well under Firestore 1MB doc limit.
async function uploadImageBlob(uid, blob, mimeType, folder = "rx") {
  return new Promise((resolve, reject) => {
    // PDFs — read as base64 data URL directly (no canvas resize)
    if (mimeType === "application/pdf") {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result); // full data URL
      reader.onerror = () => reject(new Error("Failed to read PDF"));
      reader.readAsDataURL(blob);
      return;
    }

    // Images — compress with canvas before converting to base64
    const img = new Image();
    const objectUrl = URL.createObjectURL(blob);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let { width, height } = img;
      const MAX = 1200;
      if (width > MAX || height > MAX) {
        if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
        else { width = Math.round(width * MAX / height); height = MAX; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      // Convert to base64 JPEG data URL — this is what gets saved in Firestore
      const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
      console.log("Image compressed, size:", Math.round(dataUrl.length / 1024), "KB");
      resolve(dataUrl);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load image for compression"));
    };

    img.src = objectUrl;
  });
}

// ─── MINI COMPONENTS ─────────────────────────────────────────────────────────

function Card({ t, children, style = {}, onClick }) {
  return (
    <div onClick={onClick} style={{ background:t.card, borderRadius:16, padding:16, marginBottom:12, boxShadow:"0 2px 12px rgba(0,0,0,0.06)", border:`1px solid ${t.secondary}30`, cursor:onClick?"pointer":"default", ...style }}>
      {children}
    </div>
  );
}

function Btn({ t, children, onClick, variant="primary", style={}, disabled=false }) {
  const bg  = variant==="primary" ? t.grad : variant==="danger" ? "#ffebee" : t.soft;
  const col = variant==="primary" ? "#fff"  : variant==="danger" ? "#c62828" : t.accent;
  return <button onClick={onClick} disabled={disabled} style={{ background:bg, color:col, border:"none", borderRadius:12, padding:"11px 18px", fontWeight:700, fontSize:13, cursor:disabled?"not-allowed":"pointer", fontFamily:"inherit", opacity:disabled?0.6:1, ...style }}>{children}</button>;
}

function Input({ label, value, onChange, type="text", placeholder="" }) {
  return (
    <div style={{ marginBottom:12 }}>
      {label && <div style={{ fontSize:12, color:"#888", marginBottom:4 }}>{label}</div>}
      <input type={type} value={value||""} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        style={{ width:"100%", padding:"10px 12px", border:"1px solid #ddd", borderRadius:10, fontSize:14, fontFamily:"inherit", boxSizing:"border-box" }} />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom:12 }}>
      {label && <div style={{ fontSize:12, color:"#888", marginBottom:4 }}>{label}</div>}
      <select value={value||""} onChange={e=>onChange(e.target.value)}
        style={{ width:"100%", padding:"10px 12px", border:"1px solid #ddd", borderRadius:10, fontSize:14, fontFamily:"inherit", boxSizing:"border-box" }}>
        {options.map(o => <option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
      </select>
    </div>
  );
}

function Textarea({ label, value, onChange, rows=3 }) {
  return (
    <div style={{ marginBottom:12 }}>
      {label && <div style={{ fontSize:12, color:"#888", marginBottom:4 }}>{label}</div>}
      <textarea value={value||""} onChange={e=>onChange(e.target.value)} rows={rows}
        style={{ width:"100%", padding:"10px 12px", border:"1px solid #ddd", borderRadius:10, fontSize:14, fontFamily:"inherit", resize:"vertical", boxSizing:"border-box" }} />
    </div>
  );
}

function Modal({ t, title, onClose, children }) {
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:200, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:t.card, borderRadius:"24px 24px 0 0", padding:"20px 20px 44px", width:"100%", maxWidth:430, maxHeight:"88vh", overflowY:"auto" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
          <div style={{ fontWeight:700, fontSize:16, color:t.text }}>{title}</div>
          <button onClick={onClose} style={{ background:t.soft, border:"none", borderRadius:10, width:32, height:32, cursor:"pointer", fontSize:16, color:t.muted }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ProgressRing({ pct, size=56, stroke=5, color }) {
  const r=(size-stroke)/2, circ=2*Math.PI*r, dash=circ*Math.min(pct/100,1);
  return (
    <svg width={size} height={size} style={{ transform:"rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#eee" strokeWidth={stroke}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"/>
    </svg>
  );
}

// ─── PROGRESS ANALYSIS PANEL ─────────────────────────────────────────────────

function ProgressAnalysis({ t, rxList }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  // Need at least 2 prescriptions to compare
  const canAnalyse = rxList.length >= 2;
  const latest   = rxList[0];
  const previous = rxList[1];

  const runAnalysis = async () => {
    setLoading(true); setError(""); setAnalysis(null);
    try {
      const res = await fetch("https://eyerx-scan.alanahmad81.workers.dev", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "analyse-progress", previousRx: previous, currentRx: latest }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Analysis failed");
      if (data.parsed) setAnalysis(data.parsed);
      else throw new Error("Could not parse AI response");
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const trendIcon  = { improving:"📈", stable:"➡️", regressing:"📉", mixed:"🔄" };
  const trendColor = { improving:"#4CAF50", stable:"#FF9800", regressing:"#E53935", mixed:"#9C27B0" };
  const trendLabel = { improving:"Improving 🎉", stable:"Stable", regressing:"Needs attention", mixed:"Mixed results" };

  if (!canAnalyse) {
    return (
      <Card t={t} style={{ background: t.soft }}>
        <div style={{ fontWeight:700, fontSize:14, color:t.text, marginBottom:6 }}>📊 Vision Progress Analysis</div>
        <div style={{ fontSize:12, color:t.muted }}>
          Upload at least <strong>2 prescriptions</strong> to compare your Ortho-K progress over time.
        </div>
      </Card>
    );
  }

  return (
    <Card t={t}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:14, color:t.text }}>📊 Vision Progress Analysis</div>
          <div style={{ fontSize:11, color:t.muted }}>
            Comparing {previous.examDate} → {latest.examDate}
          </div>
        </div>
        <Btn t={t} onClick={runAnalysis} disabled={loading} style={{ padding:"8px 14px", fontSize:12 }}>
          {loading ? "Analysing…" : analysis ? "Re-analyse" : "Analyse"}
        </Btn>
      </div>

      {loading && (
        <div style={{ padding:"16px 0", textAlign:"center" }}>
          <div style={{ fontSize:13, color:t.muted, marginBottom:8 }}>AI is comparing your prescriptions…</div>
          <div style={{ height:4, borderRadius:2, background:t.soft, overflow:"hidden" }}>
            <div style={{ height:"100%", borderRadius:2, background:t.grad, width:"60%", animation:"scanbar 1.4s ease-in-out infinite" }} />
          </div>
          <style>{`@keyframes scanbar{0%{transform:translateX(-100%)}100%{transform:translateX(260%)}}`}</style>
        </div>
      )}

      {error && (
        <div style={{ fontSize:12, color:"#c62828", background:"#ffebee", borderRadius:9, padding:"8px 12px" }}>⚠️ {error}</div>
      )}

      {analysis && (
        <div style={{ animation:"fadeIn 0.3s ease" }}>
          {/* Overall trend badge */}
          <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px", background:t.soft, borderRadius:12, marginBottom:12 }}>
            <span style={{ fontSize:28 }}>{trendIcon[analysis.overallTrend] || "🔍"}</span>
            <div>
              <div style={{ fontWeight:800, fontSize:15, color:trendColor[analysis.overallTrend] || t.text }}>
                {trendLabel[analysis.overallTrend] || analysis.overallTrend}
              </div>
              <div style={{ fontWeight:600, fontSize:13, color:t.text }}>{analysis.headline}</div>
            </div>
          </div>

          {/* Per-eye breakdown */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
            {["rightEye","leftEye"].map((eye, i) => {
              const e = analysis[eye]; if (!e) return null;
              const c = trendColor[e.trend] || t.primary;
              return (
                <div key={eye} style={{ background:t.soft, borderRadius:10, padding:"10px" }}>
                  <div style={{ fontSize:10, fontWeight:700, color:t.accent, marginBottom:4 }}>{i===0?"OD Right":"OS Left"}</div>
                  <div style={{ fontSize:12, fontWeight:700, color:c }}>{trendIcon[e.trend]} {trendLabel[e.trend]}</div>
                  {e.sphereChange !== undefined && (
                    <div style={{ fontSize:11, color:t.muted, marginTop:3 }}>
                      SPH {e.sphereChange > 0 ? `+${e.sphereChange}` : e.sphereChange}D
                      {e.cylinderChange !== 0 && e.cylinderChange !== undefined ? ` · CYL ${e.cylinderChange > 0 ? `+${e.cylinderChange}` : e.cylinderChange}` : ""}
                    </div>
                  )}
                  <div style={{ fontSize:11, color:t.muted, marginTop:3 }}>{e.summary}</div>
                </div>
              );
            })}
          </div>

          {/* Clinical analysis */}
          <div style={{ fontSize:13, color:t.text, lineHeight:1.7, marginBottom:12 }}>
            {analysis.analysis}
          </div>

          {/* Motivation / encouragement */}
          <div style={{
            background: analysis.overallTrend === "improving" ? "#E8F5E9" : analysis.overallTrend === "regressing" ? "#FFF8E1" : t.soft,
            borderRadius:12, padding:"12px 14px", marginBottom:12,
            borderLeft:`4px solid ${trendColor[analysis.overallTrend] || t.primary}`,
          }}>
            <div style={{ fontWeight:700, fontSize:13, color:t.text, marginBottom:4 }}>
              {analysis.overallTrend === "improving" ? "🌟 Keep it up!" : analysis.overallTrend === "regressing" ? "💪 Don't give up!" : "💬 Your optometrist says:"}
            </div>
            <div style={{ fontSize:13, color:t.text, lineHeight:1.7 }}>{analysis.motivation}</div>
          </div>

          {/* Tips */}
          {analysis.tips?.length > 0 && (
            <div>
              <div style={{ fontWeight:700, fontSize:13, color:t.text, marginBottom:8 }}>💡 Tips for better results</div>
              {analysis.tips.map((tip, i) => (
                <div key={i} style={{ display:"flex", gap:8, alignItems:"flex-start", marginBottom:6, fontSize:12, color:t.text }}>
                  <span style={{ color:t.primary, fontWeight:700, flexShrink:0 }}>{i+1}.</span>
                  <span>{tip}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── AUTH ────────────────────────────────────────────────────────────────────

function AuthScreen({ t }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState(""); const [pass, setPass] = useState("");
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);

  const handle = async () => {
    setErr(""); setLoading(true);
    try {
      if (mode==="login") await signInWithEmailAndPassword(auth, email, pass);
      else await createUserWithEmailAndPassword(auth, email, pass);
    } catch (e) { setErr(e.message.replace("Firebase: ","").replace(/\s*\(auth.*\)/,"")); }
    setLoading(false);
  };
  const handleGoogle = async () => {
    setErr(""); setLoading(true);
    try { await signInWithPopup(auth, googleProvider); }
    catch (e) { setErr(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight:"100vh", background:t.bg, display:"flex", alignItems:"center", justifyContent:"center", padding:20, fontFamily:"inherit" }}>
      <div style={{ width:"100%", maxWidth:380 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ fontSize:52, marginBottom:8 }}>👁️</div>
          <div style={{ fontWeight:800, fontSize:28, background:t.grad, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>EyeRx</div>
          <div style={{ fontSize:13, color:t.muted, marginTop:4 }}>Your personal eye care companion</div>
        </div>
        <Card t={t} style={{ padding:24 }}>
          <div style={{ display:"flex", marginBottom:20, background:t.soft, borderRadius:12, padding:3 }}>
            {["login","signup"].map(m => (
              <button key={m} onClick={()=>setMode(m)} style={{ flex:1, padding:"9px", border:"none", borderRadius:10, fontWeight:700, fontSize:13, cursor:"pointer", background:mode===m?t.card:"transparent", color:mode===m?t.primary:t.muted, transition:"all 0.2s", fontFamily:"inherit" }}>
                {m==="login"?"Sign in":"Create account"}
              </button>
            ))}
          </div>
          <Input label="Email address" value={email} onChange={setEmail} type="email" placeholder="you@example.com"/>
          <Input label="Password" value={pass} onChange={setPass} type="password" placeholder="••••••••"/>
          {err && <div style={{ color:"#c62828", fontSize:12, marginBottom:14, padding:"9px 12px", background:"#ffebee", borderRadius:9 }}>{err}</div>}
          <Btn t={t} onClick={handle} disabled={loading} style={{ width:"100%", padding:14, marginBottom:12 }}>
            {loading?"Please wait…":mode==="login"?"Sign in":"Create account"}
          </Btn>
          <div style={{ textAlign:"center", fontSize:12, color:t.muted, margin:"8px 0" }}>or continue with</div>
          <button onClick={handleGoogle} disabled={loading}
            style={{ width:"100%", padding:13, borderRadius:12, border:`1px solid ${t.secondary}`, background:t.soft, color:t.text, fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>
            🔵 Google
          </button>
        </Card>
      </div>
    </div>
  );
}

// ─── RX MODAL ────────────────────────────────────────────────────────────────

function RxEyeForm({ eye, data, onChange }) {
  const u = (k, v) => onChange({ ...data, [k]:v });
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontWeight:700, fontSize:12, color:"#666", marginBottom:8, textTransform:"uppercase" }}>{eye==="right"?"OD — Right eye":"OS — Left eye"}</div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6 }}>
        {["sphere","cylinder","axis"].map(k => <Input key={k} label={k.toUpperCase()} value={data?.[k]} onChange={v=>u(k,v)}/>)}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6 }}>
        {["add","bc","dia"].map(k => <Input key={k} label={k.toUpperCase()} value={data?.[k]} onChange={v=>u(k,v)}/>)}
      </div>
    </div>
  );
}

function RxModal({ t, initial, onSave, onClose, uid }) {
  const [rx, setRx] = useState(initial || { right:{}, left:{}, pd:"", examDate:"", expiryDate:"", doctor:"", clinic:"", notes:"", lensType:"Ortho-K", imageUrl:"" });
  const [uploading, setUploading] = useState(false);

  const handleImage = async (file) => {
    setUploading(true);
    try {
      // Compress then upload
      const { compressImage } = await import("./ScanScreen").catch(() => ({ compressImage: null }));
      let blob = file, mimeType = file.type;
      if (file.type !== "application/pdf") {
        // inline compress
        const canvas = document.createElement("canvas");
        const img = await new Promise((res, rej) => { const i = new Image(); i.onload=()=>res(i); i.onerror=rej; i.src=URL.createObjectURL(file); });
        let {width, height} = img; const max=1200;
        if (width>max||height>max) { if(width>height){height=Math.round(height*max/width);width=max;}else{width=Math.round(width*max/height);height=max;} }
        canvas.width=width; canvas.height=height; canvas.getContext("2d").drawImage(img,0,0,width,height);
        blob = await new Promise(r=>canvas.toBlob(r,"image/jpeg",0.82));
        mimeType = "image/jpeg";
      }
      const url = await uploadImageBlob(uid, blob, mimeType, "rx");
      setRx(p => ({ ...p, imageUrl:url }));
      toast("Image uploaded ✓","success");
    } catch (e) { toast("Upload failed: "+e.message,"error"); }
    setUploading(false);
  };

  return (
    <Modal t={t} title={initial?.id?"Edit prescription":"Add prescription"} onClose={onClose}>
      <RxEyeForm eye="right" data={rx.right} onChange={v=>setRx(p=>({...p,right:v}))}/>
      <RxEyeForm eye="left"  data={rx.left}  onChange={v=>setRx(p=>({...p,left:v}))}/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
        <Input label="PD (mm)" value={rx.pd} onChange={v=>setRx(p=>({...p,pd:v}))}/>
        <Select label="Lens type" value={rx.lensType} onChange={v=>setRx(p=>({...p,lensType:v}))} options={LENS_TYPES}/>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
        <Input label="Exam date" value={rx.examDate} onChange={v=>setRx(p=>({...p,examDate:v}))} type="date"/>
        <Input label="Expiry date" value={rx.expiryDate} onChange={v=>setRx(p=>({...p,expiryDate:v}))} type="date"/>
      </div>
      <Input label="Doctor" value={rx.doctor} onChange={v=>setRx(p=>({...p,doctor:v}))}/>
      <Input label="Clinic / hospital" value={rx.clinic} onChange={v=>setRx(p=>({...p,clinic:v}))}/>
      <Textarea label="Notes" value={rx.notes} onChange={v=>setRx(p=>({...p,notes:v}))}/>
      <div style={{ marginBottom:16 }}>
        <div style={{ fontSize:12, color:"#888", marginBottom:6 }}>Prescription image</div>
        {rx.imageUrl && (
          <div style={{ marginBottom:8 }}>
            <img src={rx.imageUrl} alt="Rx" style={{ width:"100%", borderRadius:10, maxHeight:140, objectFit:"cover", display:"block", marginBottom:6 }}/>
            <button onClick={()=>{
                const a=document.createElement("a");
                a.href=rx.imageUrl;
                a.download=`prescription_${rx.examDate||"image"}.${rx.imageUrl.startsWith("data:application/pdf")?"pdf":"jpg"}`;
                a.click();
              }}
              style={{ display:"block",textAlign:"center",fontSize:12,color:t.primary,fontWeight:600,padding:"6px",background:t.soft,borderRadius:8,border:"none",cursor:"pointer",width:"100%" }}>
              ⬇️ Download image
            </button>
          </div>
        )}
        <label style={{ display:"block", padding:10, background:t.soft, borderRadius:10, textAlign:"center", cursor:"pointer", fontSize:13, color:t.primary, fontWeight:600 }}>
          {uploading?"Uploading…":"📷 Upload / replace image"}
          <input type="file" accept="image/*" style={{ display:"none" }} onChange={e=>e.target.files[0]&&handleImage(e.target.files[0])}/>
        </label>
      </div>
      <Btn t={t} onClick={()=>{ if(!rx.examDate){toast("Exam date required","error");return;} onSave(rx); onClose(); }} style={{ width:"100%" }}>
        Save prescription
      </Btn>
    </Modal>
  );
}

// ─── OTHER MODALS (unchanged from v1) ────────────────────────────────────────

function LensModal({ t, initial, onSave, onClose }) {
  const [d,setD] = useState(initial||{brand:"",type:"Ortho-K",startDate:todayISO(),eye:"Both",color:"",notes:""});
  return (
    <Modal t={t} title={initial?.id?"Edit lens":"Add lens"} onClose={onClose}>
      <Input label="Brand / product name" value={d.brand} onChange={v=>setD(p=>({...p,brand:v}))} placeholder="e.g. Paragon CRT"/>
      <Select label="Lens type" value={d.type} onChange={v=>setD(p=>({...p,type:v}))} options={LENS_TYPES}/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
        <Input label="Start date" value={d.startDate} onChange={v=>setD(p=>({...p,startDate:v}))} type="date"/>
        <Select label="Eye" value={d.eye} onChange={v=>setD(p=>({...p,eye:v}))} options={["Both","Right (OD)","Left (OS)"]}/>
      </div>
      <Input label="Colour (optional)" value={d.color} onChange={v=>setD(p=>({...p,color:v}))} placeholder="e.g. Clear"/>
      <Textarea label="Notes" value={d.notes} onChange={v=>setD(p=>({...p,notes:v}))} rows={2}/>
      <Btn t={t} onClick={()=>{onSave(d);onClose();}} style={{ width:"100%" }}>Save lens</Btn>
    </Modal>
  );
}

function ApptModal({ t, initial, onSave, onClose }) {
  const [d,setD] = useState(initial||{title:"Eye exam",date:"",time:"",doctor:"",clinic:"",notes:""});
  const [calType,setCalType] = useState(null);
  const addToCalendar = (appt) => {
    if (calType==="google") {
      const dt=appt.date.replace(/-/g,"");
      window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(appt.title)}&dates=${dt}/${dt}&details=${encodeURIComponent(`Doctor: ${appt.doctor}\nClinic: ${appt.clinic}\n${appt.notes}`)}&location=${encodeURIComponent(appt.clinic)}`,"_blank");
    } else if (calType==="local") {
      const ics=["BEGIN:VCALENDAR","VERSION:2.0","BEGIN:VEVENT",`DTSTART:${appt.date.replace(/-/g,"")}`,`SUMMARY:${appt.title}`,`DESCRIPTION:Doctor: ${appt.doctor}\\nClinic: ${appt.clinic}`,`LOCATION:${appt.clinic}`,"END:VEVENT","END:VCALENDAR"].join("\r\n");
      const a=document.createElement("a");a.href=URL.createObjectURL(new Blob([ics],{type:"text/calendar"}));a.download=`${appt.title}.ics`;a.click();
    }
  };
  return (
    <Modal t={t} title={initial?.id?"Edit appointment":"Add appointment"} onClose={onClose}>
      <Input label="Title" value={d.title} onChange={v=>setD(p=>({...p,title:v}))}/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
        <Input label="Date" value={d.date} onChange={v=>setD(p=>({...p,date:v}))} type="date"/>
        <Input label="Time" value={d.time} onChange={v=>setD(p=>({...p,time:v}))} type="time"/>
      </div>
      <Input label="Doctor" value={d.doctor} onChange={v=>setD(p=>({...p,doctor:v}))}/>
      <Input label="Clinic / hospital" value={d.clinic} onChange={v=>setD(p=>({...p,clinic:v}))}/>
      <Textarea label="Notes" value={d.notes} onChange={v=>setD(p=>({...p,notes:v}))} rows={2}/>
      <div style={{ marginBottom:16 }}>
        <div style={{ fontSize:12, color:"#888", marginBottom:8 }}>Add to calendar (optional)</div>
        <div style={{ display:"flex", gap:8 }}>
          {[["google","🗓 Google Calendar"],["local","📅 Device Calendar"]].map(([v,l])=>(
            <button key={v} onClick={()=>setCalType(calType===v?null:v)}
              style={{ flex:1, padding:"9px 6px", border:`1.5px solid ${calType===v?t.primary:"#ddd"}`, borderRadius:10, background:calType===v?t.soft:"#fff", color:calType===v?t.primary:"#555", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>{l}</button>
          ))}
        </div>
      </div>
      <Btn t={t} onClick={()=>{ if(!d.date){toast("Date required","error");return;} onSave(d); if(calType)addToCalendar(d); onClose(); }} style={{ width:"100%" }}>
        Save appointment
      </Btn>
    </Modal>
  );
}

function ReminderModal({ t, initial, onSave, onClose }) {
  const [d,setD] = useState(initial||{title:"",type:"Lens replacement",dueDate:todayISO(),repeatDays:"30",notes:""});
  return (
    <Modal t={t} title={initial?.id?"Edit reminder":"Add reminder"} onClose={onClose}>
      <Input label="Title" value={d.title} onChange={v=>setD(p=>({...p,title:v}))} placeholder="e.g. Replace Ortho-K lenses"/>
      <Select label="Type" value={d.type} onChange={v=>setD(p=>({...p,type:v}))} options={["Lens replacement","Solution restock","Case replacement","Appointment","Inspection","Custom"]}/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
        <Input label="Due date" value={d.dueDate} onChange={v=>setD(p=>({...p,dueDate:v}))} type="date"/>
        <Input label="Repeat every (days)" value={d.repeatDays} onChange={v=>setD(p=>({...p,repeatDays:v}))} type="number"/>
      </div>
      <Textarea label="Notes" value={d.notes} onChange={v=>setD(p=>({...p,notes:v}))} rows={2}/>
      <Btn t={t} onClick={()=>{onSave(d);onClose();}} style={{ width:"100%" }}>Save reminder</Btn>
    </Modal>
  );
}

function RestockModal({ t, initial, onSave, onClose }) {
  const [d,setD] = useState(initial||{item:"Solution",brand:"",quantity:"1",unit:"bottle",date:todayISO(),notes:""});
  return (
    <Modal t={t} title={initial?.id?"Edit restock":"Log restock"} onClose={onClose}>
      <Select label="Item" value={d.item} onChange={v=>setD(p=>({...p,item:v}))} options={["Solution","Lens case","Lenses","Enzyme cleaner","Rewetting drops","Other"]}/>
      <Input label="Brand" value={d.brand} onChange={v=>setD(p=>({...p,brand:v}))} placeholder="e.g. Menicon Progent"/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
        <Input label="Quantity" value={d.quantity} onChange={v=>setD(p=>({...p,quantity:v}))} type="number"/>
        <Input label="Unit" value={d.unit} onChange={v=>setD(p=>({...p,unit:v}))} placeholder="bottle / pair / box"/>
      </div>
      <Input label="Date purchased" value={d.date} onChange={v=>setD(p=>({...p,date:v}))} type="date"/>
      <Textarea label="Notes" value={d.notes} onChange={v=>setD(p=>({...p,notes:v}))} rows={2}/>
      <Btn t={t} onClick={()=>{onSave(d);onClose();}} style={{ width:"100%" }}>Save restock</Btn>
    </Modal>
  );
}

// ─── SETTINGS ────────────────────────────────────────────────────────────────

function SettingsScreen({ t, themeKey, setThemeKey, user, customIcons={}, onOpenIconPicker, onResetIcon }) {
  const [notifStatus,setNotifStatus] = useState(typeof Notification!=="undefined"?Notification.permission:"unsupported");
  const enableNotif = async () => {
    const result = await requestNotificationPermission();
    setNotifStatus(typeof Notification!=="undefined"?Notification.permission:result);
    if (result==="granted") toast("Notifications enabled ✓","success");
    else toast("Notifications blocked — check browser settings","error");
  };
  const statusColor = {granted:"#4CAF50",denied:"#E53935",default:"#FF9800",unsupported:"#999"}[notifStatus]||"#999";
  return (
    <div>
      <div style={{ fontWeight:700, fontSize:18, color:t.text, marginBottom:16 }}>Settings</div>
      <Card t={t}>
        <div style={{ fontWeight:700, fontSize:14, color:t.text, marginBottom:12 }}>👤 Account</div>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
          <div style={{ width:48, height:48, borderRadius:"50%", background:t.grad, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:20 }}>
            {(user.displayName||user.email||"?")[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight:600, color:t.text }}>{user.displayName||"User"}</div>
            <div style={{ fontSize:12, color:t.muted }}>{user.email}</div>
          </div>
        </div>
        <Btn t={t} variant="danger" onClick={()=>signOut(auth)} style={{ width:"100%" }}>Sign out</Btn>
      </Card>
      <Card t={t}>
        <div style={{ fontWeight:700, fontSize:14, color:t.text, marginBottom:12 }}>🎨 Theme</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
          {Object.entries(THEMES).map(([k,th])=>(
            <button key={k} onClick={()=>setThemeKey(k)} style={{ background:th.card, border:`2px solid ${k===themeKey?th.primary:"#eee"}`, borderRadius:14, padding:"12px 8px", cursor:"pointer", boxShadow:k===themeKey?`0 4px 12px ${th.primary}44`:"none" }}>
              <div style={{ height:30, borderRadius:8, background:th.grad, marginBottom:8 }}/>
              <div style={{ fontSize:12, fontWeight:k===themeKey?700:500, color:th.text }}>{th.name}</div>
            </button>
          ))}
        </div>
      </Card>
      <Card t={t}>
        <div style={{ fontWeight:700, fontSize:14, color:t.text, marginBottom:4 }}>🔔 Notifications</div>
        <div style={{ fontSize:12, color:t.muted, marginBottom:12 }}>Get reminded to replace lenses, restock solution, and attend appointments.</div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
          <div>
            <div style={{ fontSize:13, fontWeight:600, color:t.text }}>Push notifications</div>
            <span style={{ background:`${statusColor}22`, color:statusColor, padding:"2px 10px", borderRadius:12, fontSize:11, fontWeight:700 }}>
              {notifStatus==="granted"?"✓ Enabled":notifStatus==="denied"?"✗ Blocked":notifStatus==="unsupported"?"Not supported":"Not set"}
            </span>
          </div>
          {notifStatus!=="granted"&&notifStatus!=="unsupported"&&(
            <Btn t={t} onClick={enableNotif} style={{ padding:"8px 16px", fontSize:12 }}>Enable</Btn>
          )}
        </div>
        {notifStatus==="denied"&&(
          <div style={{ fontSize:12, color:"#c62828", background:"#ffebee", borderRadius:9, padding:"9px 12px", marginBottom:10 }}>
            Blocked in browser. Go to <strong>Browser → Site settings → Notifications</strong> to re-enable.
          </div>
        )}
        <div style={{ fontSize:12, color:t.muted, background:t.soft, borderRadius:9, padding:"9px 12px" }}>
          💡 On iOS, tap <strong>Share → Add to Home Screen</strong> first, then enable notifications inside the app.
        </div>
      </Card>
      <Card t={t}>
        <div style={{ fontWeight:700, fontSize:14, color:t.text, marginBottom:12 }}>🎭 Custom icons</div>
        <div style={{ fontSize:12, color:t.muted, marginBottom:14 }}>
          Tap any icon to change it. Choose from the library or type any emoji.
        </div>
        <div style={{ marginBottom:10 }}>
          <div style={{ fontSize:11, fontWeight:700, color:t.muted, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:8 }}>Stock items</div>
          {Object.keys({"Solution":1,"Lens case":1,"Lenses":1,"Enzyme cleaner":1,"Rewetting drops":1,"Other":1}).map(key=>(
            <div key={key} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:`1px solid ${t.soft}` }}>
              <button onClick={()=>onOpenIconPicker(key)}
                style={{ width:40,height:40,borderRadius:11,background:t.soft,border:`1.5px solid ${t.secondary}40`,fontSize:22,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s" }}>
                {(customIcons&&customIcons[key])||{"Solution":"🧴","Lens case":"🥽","Lenses":"👓","Enzyme cleaner":"🧪","Rewetting drops":"💧","Other":"📦"}[key]}
              </button>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13,fontWeight:600,color:t.text }}>{key}</div>
                <div style={{ fontSize:10,color:t.muted }}>{customIcons&&customIcons[key]?"Custom icon":"Default icon"}</div>
              </div>
              <div style={{ display:"flex",gap:5 }}>
                <button onClick={()=>onOpenIconPicker(key)} style={{ fontSize:11,color:t.primary,background:t.soft,border:"none",borderRadius:8,padding:"4px 9px",cursor:"pointer",fontWeight:600,fontFamily:"inherit" }}>Change</button>
                {customIcons&&customIcons[key]&&<button onClick={()=>onResetIcon(key)} style={{ fontSize:11,color:t.muted,background:"none",border:"none",cursor:"pointer",fontFamily:"inherit" }}>Reset</button>}
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginBottom:4 }}>
          <div style={{ fontSize:11, fontWeight:700, color:t.muted, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:8 }}>Reminder types</div>
          {Object.keys({"Lens replacement":1,"Solution restock":1,"Case replacement":1,"Appointment":1,"Inspection":1}).map(key=>(
            <div key={key} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:`1px solid ${t.soft}` }}>
              <button onClick={()=>onOpenIconPicker(key)}
                style={{ width:40,height:40,borderRadius:11,background:t.soft,border:`1.5px solid ${t.secondary}40`,fontSize:22,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>
                {(customIcons&&customIcons[key])||{"Lens replacement":"👓","Solution restock":"🧴","Case replacement":"🥽","Appointment":"📅","Inspection":"🔬"}[key]}
              </button>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13,fontWeight:600,color:t.text }}>{key}</div>
                <div style={{ fontSize:10,color:t.muted }}>{customIcons&&customIcons[key]?"Custom icon":"Default icon"}</div>
              </div>
              <div style={{ display:"flex",gap:5 }}>
                <button onClick={()=>onOpenIconPicker(key)} style={{ fontSize:11,color:t.primary,background:t.soft,border:"none",borderRadius:8,padding:"4px 9px",cursor:"pointer",fontWeight:600,fontFamily:"inherit" }}>Change</button>
                {customIcons&&customIcons[key]&&<button onClick={()=>onResetIcon(key)} style={{ fontSize:11,color:t.muted,background:"none",border:"none",cursor:"pointer",fontFamily:"inherit" }}>Reset</button>}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────────────────────

export default function App() {
  const [user,setUser]     = useState(undefined);
  const [themeKey,setThemeKey] = useState(()=>localStorage.getItem("eyerx-theme")||"lavender");
  const t = THEMES[themeKey];

  const [tab,setTab]       = useState("home");
  const [rxList,setRxList] = useState([]);
  const [lensList,setLensList] = useState([]);
  const [apptList,setApptList] = useState([]);
  const [remList,setRemList]   = useState([]);
  const [restockList,setRestockList] = useState([]);
  const [stockList,setStockList]     = useState([]);
  const [historyList,setHistoryList] = useState([]);

  const [modal,setModal]       = useState(null);
  const [editItem,setEditItem] = useState(null);
  const [profile,setProfile]   = useState(()=>{ try{ return JSON.parse(localStorage.getItem("eyerx-profile")||"null"); }catch{return null;} });
  const [customIcons,setCustomIcons] = useState(()=>{ try{ return JSON.parse(localStorage.getItem("eyerx-icons")||"{}"); }catch{return {};} });
  const [showProfile,setShowProfile]   = useState(false);
  const [iconPicker,setIconPicker]     = useState(null); // { key, type }
  const [pickerCat,setPickerCat]       = useState("Eye care");
  const [pickerIcon,setPickerIcon]     = useState(null);
  const [pickerCustom,setPickerCustom] = useState("");

  useEffect(()=>{ localStorage.setItem("eyerx-theme",themeKey); },[themeKey]);
  useEffect(()=>onAuthStateChanged(auth,u=>setUser(u||null)),[]);

  const uid = user?.uid;

  useEffect(()=>{
    if (!uid) return;
    const subs=[
      onSnapshot(query(userCol(uid,"prescriptions"),orderBy("createdAt","desc")),s=>setRxList(s.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(userCol(uid,"lenses"),       orderBy("createdAt","desc")),s=>setLensList(s.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(userCol(uid,"appointments"), orderBy("date")),            s=>setApptList(s.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(userCol(uid,"reminders"),    orderBy("dueDate")),         s=>setRemList(s.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(userCol(uid,"restocks"),     orderBy("createdAt","desc")),s=>setRestockList(s.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(userCol(uid,"stock"),        orderBy("createdAt","desc")),s=>setStockList(s.docs.map(d=>({id:d.id,...d.data()})))),
      onSnapshot(query(userCol(uid,"history"),      orderBy("createdAt","desc")),s=>setHistoryList(s.docs.map(d=>({id:d.id,...d.data()})))),
    ];
    return ()=>subs.forEach(u=>u());
  },[uid]);

  useEffect(()=>{
    if (!uid) return;
    return onForegroundMessage(payload=>toast(`${payload.notification?.title}: ${payload.notification?.body}`));
  },[uid]);

  // ── CRUD ──────────────────────────────────────────────────────────────────

  const logHistory = (action,detail) =>
    addDoc(userCol(uid,"history"),{action,detail,createdAt:serverTimestamp()});

  const save = async (col,data,id) => {
    const payload={...data,updatedAt:serverTimestamp()};
    if (id){ await updateDoc(userDoc(uid,col,id),payload); toast("Saved ✓"); return id; }
    else { const r=await addDoc(userCol(uid,col),{...payload,createdAt:serverTimestamp()}); toast("Added ✓","success"); return r.id; }
  };

  const del = async (col,id,label="item") => {
    if (!window.confirm(`Delete this ${label}?`)) return;
    await deleteDoc(userDoc(uid,col,id));
    toast(`${label} deleted`);
  };

  // saveRx: compresses image to base64 and stores in Firestore document (no Storage needed)
  const saveRx = async (data) => {
    let imageUrl = data.imageUrl || "";

    // Convert compressed image blob to base64 data URL
    if (data._compressedBlob) {
      try {
        toast("Saving image...");
        imageUrl = await uploadImageBlob(uid, data._compressedBlob, data._mimeType || "image/jpeg", "rx");
        toast("Image saved ✓", "success");
      } catch (e) {
        console.error("Image base64 error:", e);
        // Don't block the save — just save without image
        imageUrl = "";
        toast("Image skipped (will save without image)", "info");
      }
    }

    const { _compressedBlob, _mimeType, _previewUrl, ...clean } = data;
    const id = await save("prescriptions", { ...clean, imageUrl }, data.id);
    await logHistory("Prescription", data.id ? "Updated" : "Added new prescription");
    return id;
  };

  const saveLens = async (data) => {
    await save("lenses",data,data.id);
    await logHistory("Lens",data.id?"Updated":`Added ${data.brand||data.type}`);
    if (!data.id) {
      const sched=SCHEDULES[data.type]||SCHEDULES["Soft Monthly"];
      const autoRems=[
        {title:`Replace ${data.type} lenses`,type:"Lens replacement", dueDate:addDays(sched.lens),    repeatDays:String(sched.lens),    notes:`Auto-created for ${data.brand||data.type}`},
        {title:"Restock lens solution",       type:"Solution restock", dueDate:addDays(sched.solution),repeatDays:String(sched.solution),notes:"Auto-created reminder"},
        {title:"Replace lens case",           type:"Case replacement", dueDate:addDays(sched.lensCase),repeatDays:String(sched.lensCase),notes:"Auto-created reminder"},
        ...(sched.inspection?[{title:"Hard lens professional inspection",type:"Inspection",dueDate:addDays(sched.inspection),repeatDays:String(sched.inspection),notes:"Visit your optometrist or ophthalmologist"}]:[]),
      ];
      for (const r of autoRems)
        await addDoc(userCol(uid,"reminders"),{...r,createdAt:serverTimestamp(),updatedAt:serverTimestamp()});
      toast(`${autoRems.length} reminders created automatically`,"success");
    }
  };

  const saveAppt    = async (data)=>{ await save("appointments",data,data.id); await logHistory("Appointment",`${data.title} on ${data.date}`); };
  const saveProfile = (data)=>{ setProfile(data); localStorage.setItem("eyerx-profile",JSON.stringify(data)); toast("Profile saved ✓","success"); };
  const saveCustomIcon = (key,icon)=>{
    const updated={...customIcons,[key]:icon};
    setCustomIcons(updated);
    localStorage.setItem("eyerx-icons",JSON.stringify(updated));
    toast(`Icon updated ${icon}`,"success");
    setIconPicker(null);
  };
  const resetIcon = (key)=>{
    const updated={...customIcons}; delete updated[key];
    setCustomIcons(updated);
    localStorage.setItem("eyerx-icons",JSON.stringify(updated));
    toast("Icon reset to default");
    setIconPicker(null);
  };
  const saveStock   = async (data)=>{ await save("stock",data,data.id); await logHistory("Stock",data.id?"Updated":`Added ${data.item}`); };
  const deleteStock = async (id)=>{ if(!window.confirm("Delete this stock item?"))return; await deleteDoc(userDoc(uid,"stock",id)); toast("Deleted"); };

  // Mark a reminder as done — optionally deduct from stock, then reschedule
  const handleMarkDone = async ({ reminder, useStock, stockItem, qtyUsed }) => {
    try {
      // Deduct stock if chosen
      if (useStock && stockItem && qtyUsed > 0) {
        const newQty = Math.max(0, parseFloat(stockItem.currentQty || 0) - qtyUsed);
        await updateDoc(userDoc(uid,"stock",stockItem.id),{ currentQty:newQty, updatedAt:serverTimestamp() });
        toast(`Stock updated: ${newQty} ${stockItem.unit||""} remaining`,"success");
      }
      // Reschedule reminder to next due date if it repeats
      if (reminder.repeatDays) {
        const nextDate = addDays(todayISO(), parseInt(reminder.repeatDays));
        await updateDoc(userDoc(uid,"reminders",reminder.id),{ dueDate:nextDate, updatedAt:serverTimestamp() });
        toast(`Next reminder set for ${nextDate}`,"success");
      } else {
        // One-time reminder — delete it
        await deleteDoc(userDoc(uid,"reminders",reminder.id));
        toast("Reminder completed ✓","success");
      }
      await logHistory("Reminder",`Completed: ${reminder.title||reminder.type}`);
    } catch(e){ toast("Error: "+e.message,"error"); }
  };

  // Quick restock from stock card top-up button
  const handleLogRestock = async (stockItem) => {
    const qty = prompt(`How many ${stockItem.unit||"units"} did you buy?`,"1");
    if (!qty || isNaN(qty)) return;
    const newQty = parseFloat(stockItem.currentQty||0) + parseFloat(qty);
    await updateDoc(userDoc(uid,"stock",stockItem.id),{ currentQty:newQty, updatedAt:serverTimestamp() });
    await logHistory("Stock",`Restocked ${stockItem.item}: +${qty} (now ${newQty})`);
    toast(`Restocked! Now ${newQty} ${stockItem.unit||"units"}}`,"success");
  };
  const saveRem     = async (data)=>{ await save("reminders",  data,data.id); };
  const saveRestock = async (data)=>{ await save("restocks",   data,data.id); await logHistory("Restock",`${data.item} — ${data.brand}`); };

  if (user===undefined) return <div style={{ display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh",fontSize:40 }}>👁️</div>;
  if (!user) return <AuthScreen t={t}/>;

  const activeRx      = rxList[0];
  const activeLens    = lensList[0];
  const schedule      = SCHEDULES[activeLens?.type||"Ortho-K"];
  const lensAge       = activeLens?daysAgo(activeLens.startDate):0;
  const lensLeft      = Math.max(0,schedule.lens-lensAge);
  const lensLeftPct   = Math.max(0,((schedule.lens-lensAge)/schedule.lens)*100);
  const urgentRems    = remList.filter(r=>r.dueDate&&daysTo(r.dueDate)<=3);
  const upcomingAppts = apptList.filter(a=>a.date>=todayISO());

  const tabs=[
    {id:"home",    icon:"🏠",label:"Home"},
    {id:"rx",      icon:"📋",label:"My Rx"},
    {id:"scan",    icon:"📷",label:"Scan"},
    {id:"progress",icon:"📈",label:"Progress"},
    {id:"more",    icon:"⋯", label:"More"},
  ];

  const S={fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif"};

  return (
    <div style={{...S,background:t.bg,minHeight:"100vh",maxWidth:430,margin:"0 auto",position:"relative"}}>
      <style>{`*{box-sizing:border-box}::-webkit-scrollbar{display:none}button,input,select,textarea{font-family:inherit}@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {/* ── Header ── */}
      <div style={{ background:t.grad,paddingBottom:20 }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"52px 20px 0" }}>
          <div>
            <div style={{ color:"rgba(255,255,255,0.8)",fontSize:13 }}>Welcome back 👋</div>
            <div style={{ color:"#fff",fontWeight:800,fontSize:22 }}>EyeRx</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setShowProfile(true)} style={{ background:"rgba(255,255,255,0.2)",border:"none",borderRadius:12,width:40,height:40,fontSize:18,cursor:"pointer" }}>
              {profile?.name?profile.name[0].toUpperCase():"👤"}
            </button>
            <button onClick={()=>setTab("settings")} style={{ background:"rgba(255,255,255,0.2)",border:"none",borderRadius:12,width:40,height:40,fontSize:18,cursor:"pointer" }}>⚙️</button>
          </div>
        </div>
        {activeLens?(
          <div style={{ margin:"16px 16px 0",background:"rgba(255,255,255,0.2)",borderRadius:18,padding:"14px 16px",display:"flex",alignItems:"center",gap:14 }}>
            <div style={{ position:"relative" }}>
              <ProgressRing pct={lensLeftPct} color="#fff" size={60} stroke={6}/>
              <div style={{ position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",color:"#fff",fontWeight:800,fontSize:13 }}>{Math.round(lensLeftPct)}%</div>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ color:"rgba(255,255,255,0.8)",fontSize:12 }}>{activeLens.brand||activeLens.type}</div>
              <div style={{ color:"#fff",fontWeight:700,fontSize:15 }}>{lensLeft===0?"⚠️ Replace today!":`${lensLeft} days until replacement`}</div>
              {urgentRems.length>0&&<div style={{ color:"#ffe082",fontSize:12,marginTop:2 }}>🔔 {urgentRems.length} reminder{urgentRems.length>1?"s":""} due soon</div>}
            </div>
            <button onClick={()=>{setModal("lens");setEditItem(activeLens);}} style={{ background:"rgba(255,255,255,0.25)",border:"none",borderRadius:10,padding:"6px 12px",color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer" }}>Edit</button>
          </div>
        ):(
          <div style={{ margin:"16px 16px 0",background:"rgba(255,255,255,0.15)",borderRadius:18,padding:"14px 16px",textAlign:"center" }}>
            <div style={{ color:"rgba(255,255,255,0.9)",fontSize:14,fontWeight:600 }}>No lens profile yet</div>
            <div style={{ color:"rgba(255,255,255,0.7)",fontSize:12,marginTop:2 }}>Add your lenses to start tracking</div>
          </div>
        )}
      </div>

      {/* ── Content ── */}
      <div style={{ padding:"18px 16px 100px",animation:"fadeIn 0.3s ease" }}>

        {/* HOME */}
        {tab==="home" && (
          <>
            <div style={{ fontWeight:700,fontSize:16,color:t.text,marginBottom:12 }}>Overview</div>

            {/* Clickable overview stat cards */}
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14 }}>
              {[
                {icon:"📋",label:"Prescriptions",val:rxList.length,  dest:"rx"},
                {icon:"👁️",label:"Lens profiles", val:lensList.length,dest:"more"},
                {icon:"📅",label:"Appointments",  val:apptList.length,dest:"more"},
                {icon:"🔔",label:"Reminders",     val:remList.length, dest:"remind"},
              ].map((s,i)=>(
                <div key={i} onClick={()=>setTab(s.dest)}
                  style={{ background:t.card,borderRadius:14,padding:14,boxShadow:"0 2px 8px rgba(0,0,0,0.05)",cursor:"pointer",transition:"transform 0.1s",userSelect:"none" }}
                  onMouseDown={e=>e.currentTarget.style.transform="scale(0.97)"}
                  onMouseUp={e=>e.currentTarget.style.transform="scale(1)"}
                  onTouchStart={e=>e.currentTarget.style.transform="scale(0.97)"}
                  onTouchEnd={e=>e.currentTarget.style.transform="scale(1)"}>
                  <div style={{ fontSize:22,marginBottom:4 }}>{s.icon}</div>
                  <div style={{ fontSize:24,fontWeight:800,color:t.text }}>{s.val}</div>
                  <div style={{ fontSize:11,color:t.muted }}>{s.label}</div>
                  <div style={{ fontSize:10,color:t.primary,marginTop:4,fontWeight:600 }}>Tap to view →</div>
                </div>
              ))}
            </div>

            {/* Quick actions */}
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14 }}>
              {[
                {icon:"📷",label:"Scan Rx",        action:()=>setTab("scan")},
                {icon:"👁️",label:"Add lens",       action:()=>{setModal("lens");setEditItem(null);}},
                {icon:"📅",label:"Add appointment",action:()=>{setModal("appt");setEditItem(null);}},
                {icon:"💧",label:"Log restock",    action:()=>{setModal("restock");setEditItem(null);}},
              ].map((a,i)=>(
                <button key={i} onClick={a.action} style={{ background:t.card,border:`1px solid ${t.secondary}30`,borderRadius:14,padding:"16px 10px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:6,boxShadow:"0 2px 8px rgba(0,0,0,0.04)" }}>
                  <span style={{ fontSize:26 }}>{a.icon}</span>
                  <span style={{ fontSize:12,fontWeight:600,color:t.text }}>{a.label}</span>
                </button>
              ))}
            </div>

            {/* Upcoming appointments preview */}
            {upcomingAppts.slice(0,2).map(a=>(
              <Card t={t} key={a.id} onClick={()=>setTab("more")}>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                  <div>
                    <div style={{ fontWeight:700,color:t.text,fontSize:14 }}>📅 {a.title}</div>
                    <div style={{ fontSize:12,color:t.muted }}>{a.date}{a.time&&` · ${a.time}`}{a.clinic&&` · ${a.clinic}`}</div>
                  </div>
                  <span style={{ fontSize:12,fontWeight:700,color:t.accent,background:t.soft,padding:"4px 10px",borderRadius:20 }}>{daysTo(a.date)===0?"Today":`${daysTo(a.date)}d`}</span>
                </div>
              </Card>
            ))}

            {/* Current Rx summary */}
            {activeRx&&(
              <Card t={t} onClick={()=>setTab("rx")}>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}>
                  <div style={{ fontWeight:700,color:t.text,fontSize:14 }}>📋 Current prescription</div>
                  <span style={{ fontSize:12,color:t.primary,fontWeight:600 }}>View →</span>
                </div>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8 }}>
                  {["right","left"].map(eye=>(
                    <div key={eye} style={{ background:t.soft,borderRadius:10,padding:"8px 10px" }}>
                      <div style={{ fontSize:10,fontWeight:700,color:t.accent,marginBottom:3 }}>{eye==="right"?"OD Right":"OS Left"}</div>
                      <div style={{ fontSize:13,fontWeight:700,color:t.text }}>SPH {fmt(+activeRx[eye]?.sphere)} CYL {activeRx[eye]?.cylinder||"—"}</div>
                    </div>
                  ))}
                </div>
                {activeRx.doctor&&<div style={{ fontSize:12,color:t.muted,marginTop:8 }}>Dr {activeRx.doctor} · {activeRx.examDate}</div>}
              </Card>
            )}

            {/* Progress analysis — shown when ≥2 Rx exist */}
            {rxList.length>=1&&(
              <ProgressAnalysis t={t} rxList={rxList}/>
            )}
          </>
        )}

        {/* SCAN */}
        {tab==="scan"&&(
          <ScanScreen t={t} onSaveRx={async(data)=>{ await saveRx(data); setTab("rx"); }}/>
        )}

        {/* MY RX */}
        {tab==="rx"&&(
          <>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
              <div style={{ fontWeight:700,fontSize:16,color:t.text }}>Prescriptions</div>
              <Btn t={t} onClick={()=>{setModal("rx");setEditItem(null);}} style={{ padding:"8px 14px",fontSize:12 }}>+ Add</Btn>
            </div>

            {/* Progress analysis in Rx tab too */}
            {rxList.length>=2&&<ProgressAnalysis t={t} rxList={rxList}/>}

            {rxList.length===0&&(
              <Card t={t} style={{ textAlign:"center",padding:32 }}>
                <div style={{ fontSize:44,marginBottom:12 }}>📋</div>
                <div style={{ fontWeight:700,color:t.text,marginBottom:6 }}>No prescriptions yet</div>
                <div style={{ fontSize:13,color:t.muted,marginBottom:18 }}>Scan your prescription with AI or add it manually.</div>
                <div style={{ display:"flex",gap:8,justifyContent:"center" }}>
                  <Btn t={t} onClick={()=>setTab("scan")} style={{ fontSize:13 }}>📷 Scan</Btn>
                  <Btn t={t} variant="outline" onClick={()=>{setModal("rx");setEditItem(null);}} style={{ fontSize:13 }}>✏️ Manual</Btn>
                </div>
              </Card>
            )}
            {rxList.map((rx,i)=>(
              <Card t={t} key={rx.id}>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12 }}>
                  <div>
                    <div style={{ fontWeight:700,color:t.text }}>{rx.doctor?`Dr ${rx.doctor}`:"Unknown doctor"}</div>
                    <div style={{ fontSize:12,color:t.muted }}>{rx.clinic}{rx.clinic&&rx.examDate?" · ":""}{rx.examDate}</div>
                  </div>
                  {i===0&&<span style={{ background:"#E8F5E9",color:"#2E7D32",fontSize:10,fontWeight:700,padding:"3px 10px",borderRadius:20 }}>Active</span>}
                </div>
                {rx.imageUrl&&(
                  <div style={{ marginBottom:12 }}>
                    <img src={rx.imageUrl} alt="Rx" style={{ width:"100%",borderRadius:10,maxHeight:140,objectFit:"cover",display:"block",marginBottom:6 }}/>
                    <button onClick={()=>{
                        const a=document.createElement("a");
                        a.href=rx.imageUrl;
                        a.download=`prescription_${rx.examDate||"image"}.${rx.imageUrl.startsWith("data:application/pdf")?"pdf":"jpg"}`;
                        a.click();
                      }}
                      style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:6,padding:"7px",background:t.soft,borderRadius:9,color:t.primary,fontSize:12,fontWeight:600,border:"none",cursor:"pointer",width:"100%" }}>
                      ⬇️ Download prescription image
                    </button>
                  </div>
                )}
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10 }}>
                  {["right","left"].map(eye=>(
                    <div key={eye} style={{ background:t.soft,borderRadius:10,padding:10 }}>
                      <div style={{ fontSize:10,fontWeight:700,color:t.accent,marginBottom:4 }}>{eye==="right"?"OD Right":"OS Left"}</div>
                      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:2 }}>
                        {["sphere","cylinder","axis"].map(f=>(
                          <div key={f} style={{ textAlign:"center" }}>
                            <div style={{ fontSize:9,color:t.muted }}>{f.slice(0,3).toUpperCase()}</div>
                            <div style={{ fontSize:12,fontWeight:700,color:t.text }}>{rx[eye]?.[f]||"—"}</div>
                          </div>
                        ))}
                      </div>
                      {rx[eye]?.bc&&<div style={{ fontSize:10,color:t.muted,marginTop:4 }}>BC {rx[eye].bc} · DIA {rx[eye].dia}</div>}
                    </div>
                  ))}
                </div>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:10 }}>
                  {[{l:"PD",v:rx.pd?`${rx.pd} mm`:"—"},{l:"Expires",v:rx.expiryDate||"—"},{l:"Lens type",v:rx.lensType||"—"}].map(f=>(
                    <div key={f.l} style={{ background:t.soft,borderRadius:9,padding:"7px 8px" }}>
                      <div style={{ fontSize:9,color:t.muted }}>{f.l}</div>
                      <div style={{ fontSize:11,fontWeight:600,color:t.text }}>{f.v}</div>
                    </div>
                  ))}
                </div>
                {rx.notes&&<div style={{ fontSize:12,color:t.muted,background:t.soft,borderRadius:9,padding:"7px 10px",marginBottom:10 }}>{rx.notes}</div>}
                <div style={{ display:"flex",gap:8 }}>
                  <Btn t={t} variant="outline" onClick={()=>{setModal("rx");setEditItem(rx);}} style={{ flex:1,fontSize:12,padding:"9px" }}>✏️ Edit</Btn>
                  <Btn t={t} variant="danger"  onClick={()=>del("prescriptions",rx.id,"prescription")} style={{ flex:1,fontSize:12,padding:"9px" }}>🗑 Delete</Btn>
                </div>
              </Card>
            ))}
          </>
        )}

        {/* REMINDERS */}
        {tab==="remind"&&(
          <InventoryScreen
            t={t}
            remList={remList}
            stockList={stockList}
            onSaveStock={saveStock}
            onSaveRem={async(d)=>{ await save("reminders",d,d.id); }}
            onDeleteStock={deleteStock}
            onDeleteRem={async(id)=>{ if(!window.confirm("Delete this reminder?"))return; await deleteDoc(userDoc(uid,"reminders",id)); toast("Deleted"); }}
            onMarkDone={handleMarkDone}
            onLogRestock={handleLogRestock}
          />
        )}

        {/* PROGRESS */}
        {tab==="progress"&&(
          <ProgressScreen t={t} rxList={rxList} profile={profile}/>
        )}

        {/* MORE */}
        {tab==="more"&&(
          <>
            {/* Lenses */}
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}>
              <div style={{ fontWeight:700,fontSize:15,color:t.text }}>👁️ Lens profiles</div>
              <Btn t={t} onClick={()=>{setModal("lens");setEditItem(null);}} style={{ padding:"6px 12px",fontSize:11 }}>+ Add</Btn>
            </div>
            {lensList.map(lens=>{
              const age=daysAgo(lens.startDate),sched=SCHEDULES[lens.type]||SCHEDULES["Ortho-K"];
              const left=Math.max(0,sched.lens-age),pct=Math.max(0,((sched.lens-age)/sched.lens)*100);
              const col=left<=2?"#E53935":left<=7?"#FB8C00":t.primary;
              return (
                <Card t={t} key={lens.id}>
                  <div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:10 }}>
                    <div style={{ position:"relative" }}>
                      <ProgressRing pct={pct} color={col} size={50} stroke={5}/>
                      <div style={{ position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",fontSize:10,fontWeight:800,color:t.text }}>{Math.round(pct)}%</div>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700,color:t.text }}>{lens.brand||lens.type}</div>
                      <div style={{ fontSize:12,color:t.muted }}>{lens.type} · {lens.eye}</div>
                      <div style={{ fontSize:12,color:left===0?"#E53935":t.muted }}>{left===0?"Replace today!":`${left} days remaining`}</div>
                    </div>
                  </div>
                  <div style={{ display:"flex",gap:8 }}>
                    <Btn t={t} variant="outline" onClick={()=>{setModal("lens");setEditItem(lens);}} style={{ flex:1,fontSize:12,padding:"8px" }}>✏️ Edit</Btn>
                    <Btn t={t} variant="danger"  onClick={()=>del("lenses",lens.id,"lens profile")} style={{ flex:1,fontSize:12,padding:"8px" }}>🗑 Delete</Btn>
                  </div>
                </Card>
              );
            })}
            {lensList.length===0&&<div style={{ fontSize:13,color:t.muted,marginBottom:16,textAlign:"center" }}>No lens profiles. Add one to start tracking.</div>}

            {/* Appointments */}
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,marginTop:10 }}>
              <div style={{ fontWeight:700,fontSize:15,color:t.text }}>📅 Appointments</div>
              <Btn t={t} onClick={()=>{setModal("appt");setEditItem(null);}} style={{ padding:"6px 12px",fontSize:11 }}>+ Add</Btn>
            </div>
            {apptList.map(a=>(
              <Card t={t} key={a.id}>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start" }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700,color:t.text }}>{a.title}</div>
                    <div style={{ fontSize:12,color:t.muted }}>{a.date}{a.time?` · ${a.time}`:""}{a.doctor?` · Dr ${a.doctor}`:""}</div>
                    {a.clinic&&<div style={{ fontSize:12,color:t.muted }}>{a.clinic}</div>}
                  </div>
                  <div style={{ display:"flex",gap:6,flexShrink:0,marginLeft:8 }}>
                    <button onClick={()=>{setModal("appt");setEditItem(a);}} style={{ background:t.soft,border:"none",borderRadius:8,padding:"6px 9px",cursor:"pointer" }}>✏️</button>
                    <button onClick={()=>del("appointments",a.id,"appointment")} style={{ background:"#ffebee",border:"none",borderRadius:8,padding:"6px 9px",cursor:"pointer" }}>🗑</button>
                  </div>
                </div>
              </Card>
            ))}
            {apptList.length===0&&<div style={{ fontSize:13,color:t.muted,marginBottom:16,textAlign:"center" }}>No appointments yet.</div>}

            {/* Restock */}
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,marginTop:10 }}>
              <div style={{ fontWeight:700,fontSize:15,color:t.text }}>💧 Restock log</div>
              <Btn t={t} onClick={()=>{setModal("restock");setEditItem(null);}} style={{ padding:"6px 12px",fontSize:11 }}>+ Add</Btn>
            </div>
            {restockList.map(r=>(
              <Card t={t} key={r.id}>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start" }}>
                  <div>
                    <div style={{ fontWeight:700,color:t.text }}>{r.item}{r.brand?` — ${r.brand}`:""}</div>
                    <div style={{ fontSize:12,color:t.muted }}>{r.quantity} {r.unit} · {r.date}</div>
                  </div>
                  <div style={{ display:"flex",gap:6 }}>
                    <button onClick={()=>{setModal("restock");setEditItem(r);}} style={{ background:t.soft,border:"none",borderRadius:8,padding:"6px 9px",cursor:"pointer" }}>✏️</button>
                    <button onClick={()=>del("restocks",r.id,"restock entry")} style={{ background:"#ffebee",border:"none",borderRadius:8,padding:"6px 9px",cursor:"pointer" }}>🗑</button>
                  </div>
                </div>
              </Card>
            ))}
            {restockList.length===0&&<div style={{ fontSize:13,color:t.muted,marginBottom:16,textAlign:"center" }}>No restock entries yet.</div>}

            {/* History */}
            <div style={{ fontWeight:700,fontSize:15,color:t.text,marginBottom:10,marginTop:10 }}>📊 Activity history</div>
            {historyList.slice(0,15).map(h=>(
              <div key={h.id} style={{ display:"flex",gap:10,alignItems:"center",padding:"9px 0",borderBottom:`1px solid ${t.soft}` }}>
                <div style={{ width:36,height:36,borderRadius:10,background:t.soft,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0 }}>
                  {h.action==="Prescription"?"📋":h.action==="Lens"?"👁️":h.action==="Appointment"?"📅":h.action==="Restock"?"💧":"🔔"}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13,fontWeight:600,color:t.text }}>{h.action} — {h.detail}</div>
                  <div style={{ fontSize:11,color:t.muted }}>{h.createdAt?.toDate?.()?.toLocaleDateString("en-MY",{day:"numeric",month:"short",year:"numeric"})}</div>
                </div>
              </div>
            ))}
            {historyList.length===0&&<div style={{ fontSize:13,color:t.muted,textAlign:"center",paddingTop:8 }}>No activity yet.</div>}
          </>
        )}

        {tab==="settings"&&(
          <SettingsScreen t={t} themeKey={themeKey} setThemeKey={k=>{setThemeKey(k);localStorage.setItem("eyerx-theme",k);}} user={user} customIcons={customIcons} onOpenIconPicker={(key)=>{setPickerIcon(null);setPickerCustom("");setPickerCat("Eye care");setIconPicker({key});}} onResetIcon={resetIcon}/>
        )}
      </div>

      {/* ── Bottom nav ── */}
      <div style={{ position:"fixed",bottom:0,left:"50%",transform:"translateX(-50%)",width:"100%",maxWidth:430,background:t.card,borderTop:`1px solid ${t.secondary}30`,display:"flex",padding:"8px 0 20px",boxShadow:"0 -4px 20px rgba(0,0,0,0.07)",zIndex:100 }}>
        {tabs.map(tb=>(
          <button key={tb.id} onClick={()=>setTab(tb.id)} style={{ flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3,background:"none",border:"none",cursor:"pointer",padding:"4px 0" }}>
            <span style={{ fontSize:tab===tb.id?24:21 }}>{tb.icon}</span>
            <span style={{ fontSize:10,fontWeight:tab===tb.id?700:500,color:tab===tb.id?t.primary:t.muted }}>{tb.label}</span>
            {tab===tb.id&&<div style={{ width:18,height:3,borderRadius:2,background:t.grad }}/>}
          </button>
        ))}
      </div>

      {/* ── Modals ── */}
      {showProfile&&<ProfileModal t={t} profile={profile} onSave={saveProfile} onClose={()=>setShowProfile(false)}/>}

      {/* Icon picker modal */}
      {iconPicker&&(
        <div onClick={()=>setIconPicker(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:t.card,borderRadius:"24px 24px 0 0",padding:"20px 20px 40px",width:"100%",maxWidth:430,maxHeight:"88vh",overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <div style={{fontWeight:700,fontSize:15,color:t.text}}>Change icon — {iconPicker.key}</div>
              <button onClick={()=>setIconPicker(null)} style={{background:t.soft,border:"none",borderRadius:9,width:30,height:30,cursor:"pointer",fontSize:13,color:t.muted}}>✕</button>
            </div>
            <div style={{fontSize:11,color:t.muted,marginBottom:12}}>Currently: {getItemIcon(iconPicker.key,customIcons)}</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
              {Object.keys(ICON_CATS).map(c=>(
                <button key={c} onClick={()=>setPickerCat(c)}
                  style={{padding:"5px 11px",borderRadius:16,border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:10,fontWeight:600,background:pickerCat===c?t.grad:t.soft,color:pickerCat===c?"#fff":t.muted}}>
                  {c}
                </button>
              ))}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:7,marginBottom:14}}>
              {(ICON_CATS[pickerCat]||[]).map(ico=>(
                <button key={ico} onClick={()=>setPickerIcon(ico)}
                  style={{width:"100%",aspectRatio:"1",borderRadius:11,border:`2px solid ${pickerIcon===ico?t.primary:"transparent"}`,background:pickerIcon===ico?t.soft:t.soft,cursor:"pointer",fontSize:20,display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s",transform:pickerIcon===ico?"scale(1.1)":"scale(1)"}}>
                  {ico}
                </button>
              ))}
            </div>
            <div style={{fontSize:11,color:t.muted,marginBottom:6,fontWeight:600}}>Or type any custom emoji</div>
            <div style={{display:"flex",gap:8,alignItems:"center",padding:"10px",background:t.soft,borderRadius:11,marginBottom:14}}>
              <input value={pickerCustom} onChange={e=>{
                const em=[...e.target.value].find(c=>/\p{Emoji}/u.test(c));
                if(em){setPickerCustom(em);setPickerIcon(em);}else setPickerCustom(e.target.value);
              }} maxLength={2} placeholder="😊"
                style={{width:52,padding:"7px",border:"1px solid #ddd",borderRadius:9,fontSize:22,textAlign:"center",fontFamily:"inherit",background:"#fff"}}/>
              <div style={{fontSize:12,color:t.text}}>Paste or type any emoji from your keyboard</div>
            </div>
            {pickerIcon&&(
              <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:t.soft,borderRadius:11,marginBottom:14}}>
                <div style={{width:44,height:44,borderRadius:12,background:t.card,display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,border:`2px solid ${t.secondary}40`}}>{pickerIcon}</div>
                <div><div style={{fontSize:12,fontWeight:700,color:t.text}}>Preview</div><div style={{fontSize:11,color:t.muted}}>{iconPicker.key}</div></div>
              </div>
            )}
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>resetIcon(iconPicker.key)}
                style={{flex:1,padding:"11px",borderRadius:12,border:`1px solid ${t.secondary}40`,background:t.soft,color:t.muted,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
                Reset to default
              </button>
              <button onClick={()=>{if(pickerIcon)saveCustomIcon(iconPicker.key,pickerIcon);}} disabled={!pickerIcon}
                style={{flex:2,padding:"11px",borderRadius:12,border:"none",background:pickerIcon?t.grad:"#ddd",color:pickerIcon?"#fff":"#999",fontWeight:700,fontSize:13,cursor:pickerIcon?"pointer":"not-allowed",fontFamily:"inherit"}}>
                Save icon
              </button>
            </div>
          </div>
        </div>
      )}

      {modal==="rx"       &&<RxModal       t={t} initial={editItem} onSave={saveRx}      onClose={()=>setModal(null)} uid={uid}/>}
      {modal==="lens"     &&<LensModal     t={t} initial={editItem} onSave={saveLens}    onClose={()=>setModal(null)}/>}
      {modal==="appt"     &&<ApptModal     t={t} initial={editItem} onSave={saveAppt}    onClose={()=>setModal(null)}/>}

      {modal==="restock"  &&<RestockModal  t={t} initial={editItem} onSave={saveRestock} onClose={()=>setModal(null)}/>}
    </div>
  );
}
